// Panel admin: timeline de eventos entrantes del webhook ML
// Filtros por topic + estado (sin procesar / OK / error)
// Sirve para debugear y monitorear que las ventas/preguntas/etc lleguen bien.

import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import AdminLayout from '../../components/AdminLayout'

const COLOR_TOPIC = {
  orders_v2: { bg: '#DCFCE7', color: '#166534', label: '🛒 Venta' },
  messages: { bg: '#DBEAFE', color: '#1E40AF', label: '💬 Mensaje' },
  questions: { bg: '#FEF3C7', color: '#92400E', label: '❓ Pregunta' },
  claims: { bg: '#FEE2E2', color: '#7F1D1D', label: '⚠ Reclamo' },
  payments: { bg: '#F3E8FF', color: '#7C3AED', label: '💰 Pago' },
  shipments: { bg: '#E0F2FE', color: '#0C4A6E', label: '📦 Envío' },
  items: { bg: '#F1F5F9', color: '#475569', label: '📋 Item' },
  'stock-locations': { bg: '#F1F5F9', color: '#475569', label: '📊 Stock' },
}

export default function EventosML() {
  const [eventos, setEventos] = useState([])
  const [loading, setLoading] = useState(true)
  const [filtro, setFiltro] = useState({ topic: 'todos', estado: 'todos' })
  const [autoRefresh, setAutoRefresh] = useState(true)

  async function cargar() {
    const q = supabase.from('ml_webhook_inbox')
      .select('*')
      .order('received_at', { ascending: false })
      .limit(100)
    const { data } = await q
    setEventos(data || [])
    setLoading(false)
  }

  useEffect(() => {
    cargar()
    if (!autoRefresh) return
    const t = setInterval(cargar, 10000)  // refresh cada 10s
    return () => clearInterval(t)
  }, [autoRefresh])

  const filtrados = useMemo(() => {
    return eventos.filter(e => {
      if (filtro.topic !== 'todos' && e.topic !== filtro.topic) return false
      if (filtro.estado === 'sin_procesar' && e.processed_at) return false
      if (filtro.estado === 'ok' && !(e.processed_at && !e.processing_error)) return false
      if (filtro.estado === 'error' && !(e.processed_at && !!e.processing_error)) return false
      return true
    })
  }, [eventos, filtro])

  const stats = useMemo(() => {
    const total = eventos.length
    const sinProc = eventos.filter(e => !e.processed_at).length
    const okay = eventos.filter(e => e.processed_at && !e.processing_error).length
    const errores = eventos.filter(e => e.processed_at && !!e.processing_error).length
    const firmasInvalidas = eventos.filter(e => e.firma_valida === false).length
    return { total, sinProc, okay, errores, firmasInvalidas }
  }, [eventos])

  return (
    <AdminLayout>
      <div className="page">
        <Link to="/admin/canales/mercadolibre" style={{ fontSize: 13, color: 'var(--muted)' }}>← Volver a MercadoLibre</Link>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginTop: 8 }}>
          <div>
            <h1 style={{ margin: 0 }}>📡 Eventos webhook ML</h1>
            <p className="lead" style={{ margin: '4px 0 0' }}>
              Notificaciones que ML nos manda en tiempo real. Útil para debugear ventas/preguntas/etc que llegan al sistema.
            </p>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#475569' }}>
            <input type="checkbox" checked={autoRefresh} onChange={e => setAutoRefresh(e.target.checked)} />
            Auto-refresh 10s
          </label>
        </div>

        {/* KPIs */}
        <div className="cards-grid" style={{ marginTop: 16 }}>
          <div className="card"><h3>Total (últimos 100)</h3><div className="stat">{stats.total}</div></div>
          <div className="card" style={{ borderLeft: '4px solid #F59E0B' }}><h3>Sin procesar</h3><div className="stat" style={{ color: '#F59E0B' }}>{stats.sinProc}</div></div>
          <div className="card" style={{ borderLeft: '4px solid #10B981' }}><h3>OK</h3><div className="stat" style={{ color: '#10B981' }}>{stats.okay}</div></div>
          <div className="card" style={{ borderLeft: '4px solid #EF4444' }}><h3>Errores</h3><div className="stat" style={{ color: '#EF4444' }}>{stats.errores}</div></div>
          {stats.firmasInvalidas > 0 && (
            <div className="card" style={{ borderLeft: '4px solid #7C3AED' }}>
              <h3>Firmas inválidas</h3>
              <div className="stat" style={{ color: '#7C3AED' }}>{stats.firmasInvalidas}</div>
              <div className="desc">Revisar client_secret</div>
            </div>
          )}
        </div>

        {/* Aviso URL del webhook */}
        <div style={{
          marginTop: 16, padding: 14,
          background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 10,
          fontSize: 12, color: '#475569',
        }}>
          <strong style={{ color: '#1A3461' }}>URL para configurar en dev.mercadolibre.com → tu app → Notificaciones:</strong>
          {/* OJO: va la URL del PROXY en dominio propio, NO la de Supabase. ML rechaza dominios
              cloud (supabase.co, vercel, etc) en su validador — por eso existe el Worker en
              limpack.uy/ml-webhook, que reenvía a la edge function. Hasta el 26-jul-2026 este
              cartel mostraba la URL directa de Supabase e inducía a configurarla mal. */}
          <div style={{ marginTop: 4, fontFamily: 'monospace', background: '#fff', padding: '6px 10px', borderRadius: 6, border: '1px solid #CBD5E1', wordBreak: 'break-all' }}>
            https://limpack.uy/ml-webhook
          </div>
          <div style={{ marginTop: 6, fontSize: 11, color: '#94A3B8' }}>
            Topics recomendados: <code>orders_v2</code>, <code>messages</code>, <code>questions</code>, <code>claims</code>, <code>payments</code>, <code>shipments</code>.
          </div>
          <div style={{ marginTop: 8, fontSize: 11.5, color: '#92400E', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, padding: '8px 10px', lineHeight: 1.5 }}>
            ⚠ Tiene que ser el dominio propio (<code>limpack.uy</code>). ML rechaza las URLs de
            Supabase en su validador — igual que MercadoPago, que usa <code>limpack.uy/mp-webhook</code>.
          </div>
        </div>

        {/* Filtros */}
        <div style={{ display: 'flex', gap: 10, marginTop: 18, flexWrap: 'wrap' }}>
          <select value={filtro.topic} onChange={e => setFiltro({ ...filtro, topic: e.target.value })}
            style={{ padding: '6px 10px', border: '1px solid #CBD5E1', borderRadius: 6, fontSize: 13 }}>
            <option value="todos">Todos los topics</option>
            <option value="orders_v2">🛒 Ventas</option>
            <option value="messages">💬 Mensajes</option>
            <option value="questions">❓ Preguntas</option>
            <option value="claims">⚠ Reclamos</option>
            <option value="payments">💰 Pagos</option>
            <option value="shipments">📦 Envíos</option>
            <option value="items">📋 Items</option>
          </select>
          <select value={filtro.estado} onChange={e => setFiltro({ ...filtro, estado: e.target.value })}
            style={{ padding: '6px 10px', border: '1px solid #CBD5E1', borderRadius: 6, fontSize: 13 }}>
            <option value="todos">Todos los estados</option>
            <option value="sin_procesar">Sin procesar</option>
            <option value="ok">Procesado OK</option>
            <option value="error">Con error</option>
          </select>
        </div>

        {/* Timeline */}
        {loading ? (
          <div className="card" style={{ marginTop: 16, color: 'var(--muted)' }}>Cargando…</div>
        ) : filtrados.length === 0 ? (
          <div className="card" style={{ marginTop: 16, color: 'var(--muted)', textAlign: 'center', padding: 28 }}>
            <div style={{ fontSize: 36, opacity: 0.4 }}>📡</div>
            <p style={{ margin: '10px 0 4px' }}>No hay eventos todavía con esos filtros.</p>
            <p style={{ margin: 0, fontSize: 11, color: '#94A3B8' }}>
              Si configuraste el webhook en ML, los eventos van a aparecer acá apenas suceda algo.
            </p>
          </div>
        ) : (
          <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {filtrados.map(e => {
              const c = COLOR_TOPIC[e.topic] || { bg: '#F1F5F9', color: '#475569', label: e.topic }
              const ts = new Date(e.received_at).toLocaleString('es-UY', { dateStyle: 'short', timeStyle: 'medium' })
              return (
                <details key={e.id} style={{
                  background: '#fff', border: `1px solid ${e.firma_valida === false ? '#FCD34D' : '#E2E8F0'}`,
                  borderRadius: 10, padding: 12,
                }}>
                  <summary style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10, listStyle: 'none', flexWrap: 'wrap' }}>
                    <span style={{
                      fontSize: 11, padding: '3px 9px', borderRadius: 999,
                      background: c.bg, color: c.color, fontWeight: 800,
                      letterSpacing: '.04em', whiteSpace: 'nowrap',
                    }}>{c.label}</span>
                    <code style={{ fontSize: 11, color: '#475569', fontFamily: 'monospace' }}>{e.resource}</code>
                    {!e.processed_at && (
                      <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: '#FEF3C7', color: '#92400E', fontWeight: 700 }}>SIN PROCESAR</span>
                    )}
                    {e.processed_at && !e.processing_error && (
                      <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: '#DCFCE7', color: '#166534', fontWeight: 700 }}>OK</span>
                    )}
                    {e.processed_at && !!e.processing_error && (
                      <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: '#FEE2E2', color: '#7F1D1D', fontWeight: 700 }}>ERROR</span>
                    )}
                    {e.firma_valida === false && (
                      <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: '#FEF3C7', color: '#92400E', fontWeight: 700 }}>⚠ FIRMA INVÁLIDA</span>
                    )}
                    <span style={{ marginLeft: 'auto', fontSize: 11, color: '#94A3B8' }}>{ts}</span>
                  </summary>
                  <div style={{ marginTop: 10, fontSize: 11, color: '#64748B' }}>
                    {e.processing_error && (
                      <div style={{ padding: 8, background: '#FEE2E2', borderRadius: 6, marginBottom: 6, color: '#7F1D1D' }}>
                        <strong>Error:</strong> {e.processing_error}
                      </div>
                    )}
                    <pre style={{ background: '#F8FAFC', padding: 10, borderRadius: 6, fontSize: 10.5, overflow: 'auto', maxHeight: 240 }}>
                      {JSON.stringify(e.raw_payload, null, 2)}
                    </pre>
                  </div>
                </details>
              )
            })}
          </div>
        )}
      </div>
    </AdminLayout>
  )
}
